import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  {
    id: "graph1",
    title: "Análisis general",
    menuTitle: "General",
    text: `En 2024, casi un 40% de las niñas y niños viven en al menos una situación de pobreza en sus hogares. Un 13% de las niñas y niños vive en hogares en pobreza 
          multidimensional, un 15% vive en hogares con pobreza por ingreso y un 10% en pobreza  severa. Desde 2022, las niñas y niños que viven en hogares solo con pobreza 
          multidimensional disminuyeron 1 punto porcentual y la pobreza severa en 3 puntos.`,
    link: "https://public.flourish.studio/visualisation/27210744/embed",
  },
  {
    id: "graph2",
    title: "Análisis por sexo",
    menuTitle: "\u2022 Sexo",
    text: `No hay diferencias en la proporción de niñas y niños en hogares con algún tipo de pobreza según el sexo, ni en 2022 y en 2024.`,
    link: "https://public.flourish.studio/visualisation/27213596/embed",
  },
  {
    id: "graph3",
    title: "Análisis por edad",
    menuTitle: "\u2022 Edad",
    text: `La primera infancia constituye una etapa crucial para el desarrollo de habilidades cognitivas y socioemocionales, las cuales sientan las bases para el futuro 
          desempeño escolar, la inserción laboral y la autorrealización personal. Durante este período, la falta de acceso a recursos materiales básicos compromete el 
          desarrollo integral, generando impactos negativos que perduran a lo largo del ciclo vital[7]. 

  Dado que los efectos de la pobreza varían según la etapa de desarrollo en que se experimenten, resulta fundamental considerar esta especificidad y avanzar hacia una medición 
          de la pobreza infantil que incorpore dichas dimensiones. Sin embargo, bajo la actual estimación de pobreza a nivel de hogar, no se observan diferencias estadísticamente 
          significativas en la incidencia entre niñas y niños en ninguno de los años analizados.`,
    link: "https://public.flourish.studio/visualisation/27213809/embed",
  },
  {
    id: "graph4",
    title: "Análisis por país de nacimiento",
    menuTitle: "\u2022 País de nacimiento",
    text: `En 2024, según el país de nacimiento de las niñas y niños solo se observan diferencias estadísticamente significativas en pobreza multidimensional y en pobreza severa. 
          La proporción de niñas y niños en hogares con pobreza por ingreso es igual en ambos grupos. Estas diferencias hablan de las distintas dinámicas de la pobreza y de cómo 
          se agudiza en algunos grupos que históricamente han sido más vulnerables, como las niñas y niños que nacieron en el extranjero.`,
    link: "https://public.flourish.studio/visualisation/27213848/embed",
  },
  {
    id: "graph5",
    title: "Análisis por pertenencia a pueblo originario",
    menuTitle: "\u2022 Pueblo originario",
    text: `En 2024, se observa una brecha de 10 puntos porcentuales en la proporción de niñas y niños que residen en hogares con algún tipo de pobreza, según su pertenencia a 
          pueblos originarios. Quienes se identifican con un pueblo originario presentan una mayor prevalencia en todas las categorías: pobreza multidimensional, por ingresos y 
          severa.`,
    link: "https://public.flourish.studio/visualisation/27213866/embed",
  },
  {
    id: "graph6",
    title: "Análisis por quintil de ingresos",
    menuTitle: "\u2022 Quintil de ingresos",
    text: `La incidencia de la pobreza varía considerablemente según el quintil de ingresos. En 2024, las niñas y niños del primer quintil, es decir, el de menores ingresos, 
          presentan la situación más compleja: apenas un 14% reside en hogares sin pobreza. En este grupo predomina la pobreza por ingresos (44%), seguida por un 32% de niñas y 
          niños que viven en hogares con pobreza severa.

  En el segundo quintil la composición cambia: un 20% de las niñas y niños vive en hogares con pobreza multidimensional, un 13% en hogares con pobreza solo por ingresos y un 6% 
          con pobreza severa. A partir del tercer quintil, no se registran niñas y niños en situación de pobreza por ingresos ni severa; desde este punto, la proporción de hogares 
          con pobreza multidimensional desciende progresivamente, hasta volverse marginal en el quinto quintil.`,
    link: "https://public.flourish.studio/visualisation/27214107/embed",
  },
  {
    id: "graph7",
    title: "Análisis por estructura familiar",
    menuTitle: "\u2022 Estructura familiar",
    text: `Al analizar la estructura familiar, se observa que las niñas y niños en hogares nucleares biparentales presentan la mayor prevalencia de hogares sin pobreza, seguidos 
          por los extensos biparentales. En el otro extremo, se ubican los hogares monoparentales (tanto nucleares como extensos).

  Sin embargo, al interior del grupo monoparental existen diferencias relevantes: mientras que en los nucleares monoparentales predomina la pobreza por ingresos, en los extensos 
          monoparentales se observa una mayor incidencia de pobreza multidimensional.

  Esta divergencia responde a dos factores clave. Por un lado, el tamaño del hogar incide en la acumulación de carencias multidimensionales: en 2024, los hogares extensos (con 
          promedios de 4,7 a 5,6 personas) tienen mayor probabilidad de presentar privaciones que los nucleares (2,9 a 3,9 personas). Por otro lado, la capacidad de generación 
          de ingresos de los hogares monoparentales es considerablemente menor: en promedio, presentan un ingreso por adulto equivalente cercano a los $450.000, cifra que contrasta 
          con los hogares biparentales, que alcanzan los $550.000 en el caso de los extensos y los $780.000 en los nucleares.`,
    link: "https://public.flourish.studio/visualisation/27213913/embed",
  },
  {
    id: "graph8",
    title: "Análisis por zona de residencia",
    menuTitle: "\u2022 Zona",
    text: `Las niñas y niños que residen en zonas urbanas viven, en mayor proporción, en hogares sin pobreza. Esta diferencia se explica porque las niñas y niños de zonas rurales 
          habitan en mayor medida en hogares con pobreza por ingresos y con pobreza severa, registrando en ambos casos una diferencia de 3 puntos porcentuales por sobre sus pares 
          urbanos.`,
    link: "https://public.flourish.studio/visualisation/27213892/embed",
  },
  {
    id: "graph9",
    title: "Región",
    menuTitle: "\u2022 Región",
    text: `A nivel regional, Magallanes (80%) y Aysén (70%) lideran la proporción de niñas y niños en hogares sin pobreza. En contraste, las regiones que presentan una mayor 
          incidencia de niñas y niños en hogares con algún tipo de pobreza son Tarapacá (46%), La Araucanía (43%) y Atacama (42%). Tarapacá concentra, además, la mayor proporción 
          de niñas y niños en situación de pobreza severa.`,
    link: "https://public.flourish.studio/visualisation/27214197/embed",
  },
];


const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ color: 'black', paddingLeft: graph.id !== "graph1" ? "30px" : "20px" }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;
